/**
 * Where alternatives differ, section by section.
 *
 * Reported from the road, for Bangkok to Surin: "two different routes to get to
 * a point where they are the same route, and then different routes at the end.
 * but if i pick one of the routes i cant pick the last bit."
 *
 * That journey has TWO independent decisions:
 *
 *     BKK --+-- via Saraburi --+-- shared middle --+-- via 24 --+-- Surin
 *           +-- via Nakhon Nayok +                 +-- via 226 -+
 *
 * Four sensible combinations, but the router returns a handful of whole routes
 * and the sheet lists them as whole journeys. Choosing the opening you want
 * commits you to whatever ending that particular route happened to come with -
 * the two choices are welded together for no reason the driver can see.
 *
 * This module takes a set of routes and finds the SECTIONS where they disagree,
 * so each decision can be presented (and chosen) on its own. Between sections
 * every route runs over the same ground, which is exactly why they can be
 * recombined: an opening from one route and an ending from another meet at a
 * point both of them pass through.
 *
 * Deliberately geometric, not step-based. Mapbox's step lists diverge for
 * reasons that are not real forks - a step split across a junction, a road
 * renamed mid-way - whereas the coordinates either run over the same ground or
 * they do not.
 */

import type { LngLat, Route } from '../core/types';
import { distance } from '../core/geo';
import { roadNumberRefs } from './roads';

/**
 * Cell size for "the same place", in degrees. ~55 m.
 *
 * Coarse enough that the two carriageways of a divided highway, or the same
 * road sampled at different densities by two requests, count as one road.
 * Finer than the 0.005 (~550 m) whole-route dedupe in directions.ts, which
 * would smear a genuine fork hundreds of metres past where the driver sees it.
 */
const CELL_DEG = 0.0005;

/**
 * How far two routes must stay apart before it counts as a real fork.
 *
 * Below this a gap is sampling noise: two geometries can sample the same curve
 * at different points and miss each other's cells for a few dozen metres.
 */
const MIN_APART_M = 400;

/**
 * How far they must run together again before the fork is considered closed.
 *
 * Higher than MIN_APART_M because briefly touching the same cell - two roads
 * crossing, or running parallel through one junction - is not a merge. Without
 * this a route that crosses its sibling twice reads as three separate choices.
 */
const MIN_TOGETHER_M = 800;

/**
 * Cap on how long one divergent section may run before it is treated as more
 * than one decision.
 *
 * Bangkok to Surin produces a single section spanning 206 km to 431 km: the
 * two corridors (Route 24 and Route 226) genuinely do not rejoin until near
 * the destination, so it IS one decision, just a long one.
 *
 * This was briefly set to 120 km, which suppressed that chip entirely and left
 * the feature invisible on exactly the journey it was built for. The cap now
 * only catches a section spanning almost the whole route, which would mean the
 * two "alternatives" share nothing and the whole-route list is the better
 * presentation anyway.
 */
const MAX_SECTION_M = 400_000;

const cell = ([lng, lat]: LngLat): string =>
  `${Math.round(lng / CELL_DEG)}:${Math.round(lat / CELL_DEG)}`;

/** One stretch where the routes in a set do not agree. */
export interface DivergentSection {
  /** Distance along the reference route where the section begins, metres. */
  fromAlong: number;
  /** ...and where every option has merged back, metres. */
  toAlong: number;
  /** Where the roads part, for the map marker. */
  forkAt: LngLat;
  /** Where they meet again; null when they only rejoin at the destination. */
  mergeAt: LngLat | null;
}

/**
 * The stretches where `other` leaves `base` and comes back.
 *
 * Walks `base` from the start, asking for each coordinate whether `other` ever
 * passes through the same cell, and records the runs of misses.
 */
export function divergentSections(base: Route, other: Route): DivergentSection[] {
  const cb = base.geometry.coordinates as LngLat[];
  const co = other.geometry.coordinates as LngLat[];
  if (cb.length < 2 || co.length < 2) return [];

  const otherCells = new Set<string>(co.map(cell));

  const out: DivergentSection[] = [];
  let along = 0;
  /** Where the current run of misses started, or -1 while on shared ground. */
  let gapFrom = -1;
  let gapFromAt: LngLat | null = null;
  let apart = 0;
  /** Metres run together since the last miss, used to close a gap. */
  let together = 0;
  let pendingEnd = -1;
  let pendingEndAt: LngLat | null = null;

  const closeGap = (): void => {
    if (gapFrom >= 0 && apart >= MIN_APART_M && gapFromAt) {
      out.push({
        fromAlong: gapFrom,
        toAlong: pendingEnd >= 0 ? pendingEnd : along,
        forkAt: gapFromAt,
        mergeAt: pendingEndAt,
      });
    }
    gapFrom = -1;
    gapFromAt = null;
    apart = 0;
    pendingEnd = -1;
    pendingEndAt = null;
  };

  for (let i = 1; i < cb.length; i++) {
    const seg = distance(cb[i - 1], cb[i]);
    along += seg;
    const shared = otherCells.has(cell(cb[i]));

    if (shared) {
      if (gapFrom >= 0) {
        // Provisionally the end of the gap - confirmed once we have stayed
        // together for MIN_TOGETHER_M.
        if (pendingEnd < 0) {
          pendingEnd = along;
          pendingEndAt = cb[i];
          together = 0;
        }
        together += seg;
        if (together >= MIN_TOGETHER_M) closeGap();
      }
      continue;
    }

    // Not shared.
    if (pendingEnd >= 0) {
      // The "merge" did not hold - same gap continuing.
      pendingEnd = -1;
      pendingEndAt = null;
      together = 0;
    }
    if (gapFrom < 0) {
      gapFrom = along - seg;
      gapFromAt = cb[i - 1];
    }
    apart += seg;
  }

  // A gap still open at the destination is a real final section.
  if (gapFrom >= 0 && apart >= MIN_APART_M && gapFromAt) {
    out.push({ fromAlong: gapFrom, toAlong: along, forkAt: gapFromAt, mergeAt: null });
  }
  return out;
}

/** One decision the driver can make, with the options available for it. */
export interface RouteChoice {
  /** Distance along the reference route where this choice is made, metres. */
  along: number;
  forkAt: LngLat;
  mergeAt: LngLat | null;
  /** Indices into the routes array that take each distinct path here. */
  options: Array<{ routeIndex: number; roads: string[] }>;
}

/**
 * Break a set of routes into the sequence of independent decisions they imply.
 *
 * Routes are compared against the first (the fastest, which the sheet already
 * treats as the reference). Sections that start at roughly the same place are
 * the same decision seen from different alternatives, so they are merged.
 *
 * Returns [] when the routes present a single choice or none - in that case the
 * ordinary whole-route list is the honest presentation and nothing changes.
 */
/**
 * How far the diverging stretches must run before the choice is worth offering.
 *
 * Measured against real route sets: Bangkok to Ubon, Chiang Mai and Nakhon
 * Phanom all produced "decisions" at 2, 5, 16 and 20 km, which are the city
 * streets leaving Bangkok - the toll-free and motorway-free variants pick
 * different ways out of town. Those are not choices a driver wants to make from
 * a sheet; they are noise that would bury the one decision that matters (Route
 * 24 vs 226 at 206 km) under four meaningless rows.
 *
 * 15 km is comfortably above city-exit variation and far below any real
 * corridor choice.
 */
const MIN_CHOICE_M = 15_000;

/**
 * How far into the journey a fork must be before it is offered as a choice.
 *
 * The first few kilometres are the router picking a way out of town, and the
 * variants disagree there constantly - Surin to Pattaya produced chips at
 * 6.9 km and 11 km, two near-identical city-exit choices that pushed the real
 * corridor decisions (85 km, 109 km) off the sheet. A driver does not plan the
 * first three turns from a chip strip.
 */
const MIN_FORK_ALONG_M = 12_000;

export function routeChoices(routes: Route[]): RouteChoice[] {
  if (routes.length < 2) return [];

  /** Sections keyed by roughly where they begin, so siblings group together. */
  const buckets = new Map<number, RouteChoice>();

  /*
   * EVERY PAIR, not just every route against the first.
   *
   * Comparing only against route A misses any fork that exists between two
   * alternatives. Reported from the road with a Bangkok-Surin map: route B
   * takes the southern approach, and near Poipet a third corridor (C) branches
   * off and rejoins the 24 - "if i pick b, i cant pick the easten route on the
   * c leg near poi pet." That fork is between B and C. A never goes near it, so
   * base-only comparison could not see it and the driver was offered no choice
   * there at all.
   *
   * Pairwise is O(n^2) in the number of routes, which is at most four here.
   */
  for (let i = 0; i < routes.length; i++) {
    for (let r = i + 1; r < routes.length; r++) {
      for (const sec of divergentSections(routes[i], routes[r])) {
        // Skip the short stuff - see MIN_CHOICE_M. Measured on the length of
        // the divergence itself, not where it starts: a 2 km detour is noise
        // whether it happens leaving town or 300 km in.
        if (sec.toAlong - sec.fromAlong < MIN_CHOICE_M) continue;
        /*
         * An over-long section is several decisions the geometry failed to
         * separate - see MAX_SECTION_M. Offering it as one chip is worse than
         * offering nothing, because it claims a single choice where the driver
         * can see on the map that there are several.
         */
        if (sec.toAlong - sec.fromAlong > MAX_SECTION_M) continue;
        /*
         * Ignore forks in the first few kilometres.
         *
         * Leaving a town, the variants pick different streets to reach the same
         * highway, and those divergences are long enough to pass the length
         * test above. Surin to Pattaya showed chips at 6.9 km and 11 km - two
         * near-identical city-exit choices - while burying the corridor
         * decisions at 85 km and 109 km that the driver was actually asking
         * about. The opening streets are the router's business.
         */
        if (sec.fromAlong < MIN_FORK_ALONG_M) continue;

        /*
         * Bucket by WHERE the fork is on the ground, not by how far along the
         * pair's own reference route it sits. With pairwise comparison the same
         * junction is found from several pairs, each measuring from a different
         * route, so a distance-based key would file one physical fork under
         * several keys and show the driver duplicate chips for it.
         */
        const key = geoKey(sec.forkAt);
        const existing = buckets.get(key);
        const target = existing ?? {
          // `along` is quoted against the FIRST route for display order only.
          along: alongAt(routes[0].geometry.coordinates as LngLat[], sec.forkAt),
          forkAt: sec.forkAt,
          mergeAt: sec.mergeAt,
          options: [],
        };
        if (!existing) buckets.set(key, target);
        // Widen to the furthest merge any sibling reaches.
        if (sec.mergeAt === null) target.mergeAt = null;

        // Both sides of this pair are options at this fork; addOption keeps
        // each route once and drops arms that turn out to share a road.
        addOption(target, i, roadsOn(routes[i], sec));
        addOption(target, r, roadsOn(routes[r], sec));
      }
    }
  }

  return (
    [...buckets.values()]
      // A "choice" with one arm is not a choice - it happens when two routes
      // diverge somewhere a third already covers.
      .filter((c) => c.options.length > 1)
      .sort((a, b) => a.along - b.along)
  );
}

/** Add a route as an option at a fork, keeping each route at most once. */
function addOption(choice: RouteChoice, routeIndex: number, roads: string[]): void {
  const seen = choice.options.find((o) => o.routeIndex === routeIndex);
  if (seen) {
    // Same route seen from another pair - keep whichever roads list is richer,
    // since one pair's section can be shorter than another's.
    if (roads.length > seen.roads.length) seen.roads = roads;
    return;
  }
  choice.options.push({ routeIndex, roads });
}

/**
 * A key for "the same junction", ~1.1 km cells.
 *
 * Deliberately coarser than CELL_DEG: two pairs rarely agree on the exact
 * coordinate where a fork begins (each measures from its own geometry), so a
 * fine key would file one junction under several entries and show duplicate
 * chips for the same decision.
 */
function geoKey([lng, lat]: LngLat): number {
  const g = 0.01;
  return Math.round(lng / g) * 100_000 + Math.round(lat / g);
}

/**
 * Numbered roads a route uses across a section, located by the section's own
 * COORDINATES.
 *
 * The section's `fromAlong`/`toAlong` are distances measured along the BASE
 * route. Handing those to a sibling was wrong: an alternative reaches the same
 * fork at a different odometer reading, so the numbers selected the wrong span
 * of its steps - which is why route B was labelled with street names from a
 * stretch it does not even drive. Projecting the fork and merge points onto
 * each route gives that route's own bounds.
 */
function roadsOn(route: Route, section: DivergentSection): string[] {
  const coords = route.geometry.coordinates as LngLat[];
  const from = alongAt(coords, section.forkAt);
  const to = section.mergeAt === null ? Infinity : alongAt(coords, section.mergeAt);
  return roadsBetween(route, { ...section, fromAlong: from, toAlong: to });
}

/** Distance along `coords` of the point nearest `target`, metres. */
function alongAt(coords: LngLat[], target: LngLat): number {
  let along = 0;
  let best = Infinity;
  let bestAlong = 0;
  for (let i = 1; i < coords.length; i++) {
    along += distance(coords[i - 1], coords[i]);
    const d = distance(coords[i], target);
    if (d < best) {
      best = d;
      bestAlong = along;
    }
  }
  return bestAlong;
}

function roadsBetween(route: Route, section: DivergentSection): string[] {
  /*
   * Roads used INSIDE the section, not the whole route.
   *
   * This previously returned the leg summaries and ignored the section
   * entirely, so every decision on a journey got the same label - Bangkok to
   * Surin showed the identical chip at 71 km, 102 km and 405 km, which told the
   * driver nothing about any of them.
   *
   * Steps carry their own length, so walking them accumulates a distance-along
   * that can be compared against the section bounds.
   */
  const refs: string[] = [];
  let along = 0;

  for (const leg of route.legs) {
    for (const step of leg.steps ?? []) {
      const start = along;
      along += step.distance ?? 0;
      // Any overlap with the section counts - a single step can span it.
      if (along < section.fromAlong || start > section.toAlong) continue;

      refs.push(...roadNumberRefs(step));
    }
  }

  return [...new Set(refs)];
}

/**
 * What distinguishes one option from the others at the same decision.
 *
 * Both arms of a fork are reached from the same corridor, so their summaries
 * share most of their route numbers and repeating them tells the driver
 * nothing. The references that appear on ONE side are the actual choice.
 */
export function distinguishingRoads(mine: string[], others: string[][], limit = 2): string[] {
  const theirs = new Set(others.flat().map((s) => s.toLowerCase()));
  const only = mine.filter((n) => !theirs.has(n.toLowerCase()));
  return (only.length ? only : mine).slice(0, limit);
}

/* ------------------------- picking legs by corridor ------------------------ */

/**
 * A waypoint that forces a route through one arm of one decision.
 *
 * The chip UI could only ever SELECT a route the router had already returned,
 * so "B's opening with C's ending" was unavailable unless Mapbox happened to
 * return that exact combination - which for Bangkok to Surin it does not.
 * Reported as: "if i pick b i cant pick the other route coming from the 24.
 * its either or. makes no sense."
 *
 * Routing through a point ON the chosen arm is what makes any combination
 * reachable: the router is free to reach that point however it likes and to
 * continue however it likes, so the driver's choices compose instead of
 * competing.
 */
export interface LegPick {
  /** Index of the decision, in the order routeChoices() returned them. */
  choiceIndex: number;
  /** Which route's arm was chosen at that decision. */
  routeIndex: number;
}

/**
 * Turn a set of leg choices into via-points for a routing request.
 *
 * The point taken is the MIDDLE of the chosen arm, not its start: the start is
 * the fork itself, which every option passes through and which therefore
 * constrains nothing. The midpoint is on one arm and one arm only, so the
 * router has to use it.
 */
export function viasForPicks(
  routes: Route[],
  choices: RouteChoice[],
  picks: LegPick[],
): LngLat[] {
  const out: LngLat[] = [];
  for (const pick of picks) {
    const choice = choices[pick.choiceIndex];
    const route = routes[pick.routeIndex];
    if (!choice || !route) continue;

    const coords = route.geometry.coordinates as LngLat[];
    const from = alongAt(coords, choice.forkAt);
    const to = choice.mergeAt === null ? routeLengthOf(coords) : alongAt(coords, choice.mergeAt);
    if (to <= from) continue;

    out.push(pointAlong(coords, (from + to) / 2));
  }
  // Via-points must be given in the order they are driven, or the router will
  // happily double back to visit them in the order supplied.
  return out;
}

function routeLengthOf(coords: LngLat[]): number {
  let d = 0;
  for (let i = 1; i < coords.length; i++) d += distance(coords[i - 1], coords[i]);
  return d;
}

/** The coordinate `target` metres along the line. */
function pointAlong(coords: LngLat[], target: number): LngLat {
  let along = 0;
  for (let i = 1; i < coords.length; i++) {
    const seg = distance(coords[i - 1], coords[i]);
    if (along + seg >= target) {
      const t = seg > 0 ? (target - along) / seg : 0;
      const a = coords[i - 1];
      const b = coords[i];
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
    along += seg;
  }
  return coords[coords.length - 1];
}
